import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Radio,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DuplicateService from "../../services/DuplicateService";
import { getCvBlob } from "../../services/FileService";

const BRAND = "#03407b";

// Champs texte libres où concaténer les valeurs a du sens (pas les champs
// atomiques comme email ou téléphone). Pour ces champs, le choix peut être un
// id (une seule valeur, radio) ou un tableau d'ids (valeurs cochées à combiner).
const COMBINABLE_FIELDS = [
  "commentaire",
  "sachezQue",
  "softskills",
  "pointsforts",
  "recommandations",
  "formations",
  "competences",
  "competencesTechniques",
];

const isEmpty = (v) => v === null || v === undefined || ["", "null", "undefined", "n/a"].includes(String(v).trim().toLowerCase());

// Une couleur stable par candidat pour repérer d'un coup d'œil à qui
// appartiennent notes et CV dans la comparaison.
const CAND_COLORS = ["#03407b", "#7B1FA2", "#EF6C00", "#2E7D32", "#C2185B", "#5D4037"];

const BRAND_GRAD = "linear-gradient(135deg, #0556a4 0%, #03407b 100%)";
const PAGE_BG = "#F6F8FA";

// En-tête de section avec pastille numérotée, style "stepper".
const SectionHeader = ({ num, title, right }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    flexWrap="wrap"
    rowGap={0.5}
    sx={{ mb: 1 }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          background: BRAND_GRAD,
          color: "#fff",
          fontSize: 13,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 6px rgba(3,64,123,.3)",
          flexShrink: 0,
        }}
      >
        {num}
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
        {title}
      </Typography>
    </Stack>
    {right}
  </Stack>
);

const fullName = (c) =>
  `${c?.fields?.prenom || c?.prenom || ""} ${c?.fields?.nom || c?.nom || ""}`.trim() ||
  "(sans nom)";

const displayValue = (v) => {
  const s = String(v);
  if (!/^https?:\/\//i.test(s)) return s;
  try {
    const u = new URL(s);
    return decodeURIComponent(`${u.hostname.replace(/^www\./, "")}${u.pathname}`).replace(/\/$/, "");
  } catch (e) {
    return s;
  }
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleDateString("fr-FR");
};

/**
 * Modale de comparaison et fusion "type git" : on choisit le candidat gardé,
 * puis la valeur retenue pour chaque champ qui diffère.
 */
const MergeDialog = ({ open, onClose, group, onMerged, snackbarShowMessage }) => {
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [candidats, setCandidats] = useState([]);
  const [fieldLabels, setFieldLabels] = useState({});
  const [keeperId, setKeeperId] = useState(null);
  const [onlyDiffs, setOnlyDiffs] = useState(true);
  const [choices, setChoices] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Sélection des notes (par groupe de notes identiques) et des CV à conserver.
  const [keepNotes, setKeepNotes] = useState({});
  const [keepFiles, setKeepFiles] = useState({});

  const ids = useMemo(
    () => (group?.candidats || []).map((c) => c.id),
    [group]
  );

  const candidatById = useMemo(() => {
    const map = {};
    candidats.forEach((c) => (map[c.id] = c));
    return map;
  }, [candidats]);

  // Recalcule les choix par défaut : valeur du gardé si non vide, sinon la
  // première valeur non vide d'un autre candidat.
  const computeDefaultChoices = (list, keeper) => {
    const next = {};
    Object.keys(fieldLabels).forEach((field) => {
      const keeperVal = keeper ? candidatByIdFrom(list, keeper)?.fields?.[field] : null;
      if (!isEmpty(keeperVal)) {
        next[field] = keeper;
        return;
      }
      const donor = list.find((c) => !isEmpty(c.fields?.[field]));
      next[field] = donor ? donor.id : keeper;
    });
    return next;
  };

  const candidatByIdFrom = (list, id) => list.find((c) => c.id === id);

  useEffect(() => {
    if (!open || ids.length === 0) return;
    let active = true;
    setLoading(true);
    DuplicateService.getMergePreview(ids)
      .then((res) => {
        if (!active) return;
        const list = res.data.candidats || [];
        const labels = res.data.fieldLabels || {};
        setCandidats(list);
        setFieldLabels(labels);
        const suggested =
          (group.candidats.find((c) => c.isSuggestedKeeper) || group.candidats[0])?.id ||
          list[0]?.id;
        setKeeperId(suggested);
      })
      .catch(() => {
        snackbarShowMessage?.("Impossible de charger le détail des candidats.", "error");
        onClose();
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // (Re)initialise les choix quand candidats/labels/keeper changent.
  useEffect(() => {
    if (candidats.length && keeperId && Object.keys(fieldLabels).length) {
      setChoices(computeDefaultChoices(candidats, keeperId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidats, keeperId, fieldLabels]);

  // Lignes à afficher : champs ayant au moins une valeur, en marquant les différences.
  const rows = useMemo(() => {
    return Object.entries(fieldLabels)
      .map(([field, label]) => {
        const values = candidats.map((c) => c.fields?.[field]);
        const hasAny = values.some((v) => !isEmpty(v));
        const distinct = new Set(values.map((v) => (isEmpty(v) ? "" : String(v).trim())));
        const nonEmptyDistinct = new Set(
          values.filter((v) => !isEmpty(v)).map((v) => String(v).trim())
        );
        return {
          field,
          label,
          hasAny,
          differs: distinct.size > 1,
          // "Tout prendre" n'a de sens que si plusieurs valeurs distinctes existent.
          combinable: COMBINABLE_FIELDS.includes(field) && nonEmptyDistinct.size > 1,
        };
      })
      .filter((r) => r.hasAny);
  }, [fieldLabels, candidats]);

  // Notes identiques (même texte, auteur, date) regroupées entre candidats :
  // permet d'afficher le diff et de dédupliquer (une seule copie conservée).
  const noteGroups = useMemo(() => {
    const map = new Map();
    candidats.forEach((c) => {
      (c.notes || []).forEach((n) => {
        const key = `${String(n.commentaire || "").trim()}|${n.auteur || ""}|${n.dateTime || ""}`;
        if (!map.has(key)) {
          map.set(key, {
            key,
            commentaire: n.commentaire,
            auteur: n.auteur,
            dateTime: n.dateTime,
            copies: [],
            candIds: [],
          });
        }
        const g = map.get(key);
        g.copies.push({ id: n.id, candId: c.id });
        if (!g.candIds.includes(c.id)) g.candIds.push(c.id);
      });
    });
    return Array.from(map.values());
  }, [candidats]);

  const fileRows = useMemo(
    () =>
      candidats.flatMap((c) =>
        (c.files || []).map((f) => ({ ...f, candId: c.id, candName: fullName(c) }))
      ),
    [candidats]
  );

  const totalNoteCopies = useMemo(
    () => noteGroups.reduce((acc, g) => acc + g.copies.length, 0),
    [noteGroups]
  );

  // Par défaut tout est conservé (une copie par note unique, tous les CV).
  useEffect(() => {
    const notesInit = {};
    noteGroups.forEach((g) => (notesInit[g.key] = true));
    setKeepNotes(notesInit);
    const filesInit = {};
    fileRows.forEach((f) => (filesInit[f.id] = true));
    setKeepFiles(filesInit);
  }, [noteGroups, fileRows]);

  const keptNotesCount = noteGroups.filter((g) => keepNotes[g.key] !== false).length;
  const keptFilesCount = fileRows.filter((f) => keepFiles[f.id] !== false).length;

  // Notes communes (chez plusieurs candidats) vs propres à un seul candidat.
  const commonNotes = useMemo(
    () => noteGroups.filter((g) => g.candIds.length > 1),
    [noteGroups]
  );
  const uniqueNotesByCandidat = useMemo(() => {
    const map = {};
    noteGroups.forEach((g) => {
      if (g.candIds.length === 1) {
        (map[g.candIds[0]] = map[g.candIds[0]] || []).push(g);
      }
    });
    return map;
  }, [noteGroups]);

  const candColor = (id) => {
    const idx = candidats.findIndex((c) => c.id === id);
    return CAND_COLORS[Math.max(0, idx) % CAND_COLORS.length];
  };

  const initialsOf = (c) =>
    fullName(c)
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?";

  // Visionneuse de CV intégrée : le fichier est chargé en blob et affiché dans
  // une iframe (les PDF se lisent sans quitter la fusion).
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const openFile = async (file) => {
    try {
      setPreviewLoading(true);
      const data = (await getCvBlob(file.name)).data;
      const ext = String(file.name).split(".").pop().toLowerCase();
      const mime = ext === "pdf" ? "application/pdf" : data.type || "application/octet-stream";
      const url = URL.createObjectURL(new Blob([data], { type: mime }));
      setPreview({ name: file.name, url });
    } catch (e) {
      snackbarShowMessage?.("Impossible d'afficher le fichier.", "error");
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const loserCount = Math.max(0, candidats.length - 1);

  // Étape 1 : validation puis ouverture de la modale de confirmation.
  const handleMerge = () => {
    if (!keeperId) return;
    if (loserCount === 0) return;
    setConfirmOpen(true);
  };

  // Étape 2 : fusion réelle une fois confirmée.
  const doMerge = async () => {
    const loserIds = candidats.map((c) => c.id).filter((id) => id !== keeperId);
    if (loserIds.length === 0) return;

    const fieldOverrides = {};
    Object.keys(fieldLabels).forEach((field) => {
      const chosen = choices[field] || keeperId;
      if (Array.isArray(chosen)) {
        // Combinaison des valeurs cochées : gardé d'abord, puis l'ordre des
        // colonnes, sans vides ni doublons exacts.
        const ordered = [
          ...candidats.filter((c) => c.id === keeperId),
          ...candidats.filter((c) => c.id !== keeperId),
        ].filter((c) => chosen.includes(c.id));
        const values = [];
        ordered.forEach((c) => {
          const v = c.fields?.[field];
          if (isEmpty(v)) return;
          const t = String(v).trim();
          if (!values.includes(t)) values.push(t);
        });
        if (values.length > 0) {
          fieldOverrides[field] = values.join("\n\n");
        }
      } else if (chosen !== keeperId) {
        fieldOverrides[field] = candidatById[chosen]?.fields?.[field] ?? null;
      }
    });

    // Une seule copie par note cochée (celle du gardé de préférence), les
    // copies en double et les notes décochées sont supprimées.
    const keepNoteIds = noteGroups
      .filter((g) => keepNotes[g.key] !== false)
      .map((g) => (g.copies.find((cp) => cp.candId === keeperId) || g.copies[0]).id);
    const keepFileIds = fileRows
      .filter((f) => keepFiles[f.id] !== false)
      .map((f) => f.id);
    // Ne pilote la sélection que si le backend a bien renvoyé les listes.
    const filesSupported = candidats.some((c) => Array.isArray(c.files));

    try {
      setMerging(true);
      await DuplicateService.mergeCandidats({
        keeperId,
        loserIds,
        fieldOverrides,
        keepNoteIds,
        ...(filesSupported ? { keepFileIds } : {}),
      });
      snackbarShowMessage?.("Candidats fusionnés avec succès.", "success");
      setConfirmOpen(false);
      onMerged?.(group);
      onClose();
    } catch (e) {
      snackbarShowMessage?.(
        e?.response?.data?.message || "Échec de la fusion des candidats.",
        "error"
      );
    } finally {
      setMerging(false);
    }
  };

  // Ids (non vides) des candidats ayant une valeur pour ce champ.
  const nonEmptyIdsFor = (field) =>
    candidats.filter((c) => !isEmpty(c.fields?.[field])).map((c) => c.id);

  // Choix par défaut d'un champ : le gardé si non vide, sinon le premier donneur.
  const defaultChoiceFor = (field) => {
    const keeperVal = candidatById[keeperId]?.fields?.[field];
    if (!isEmpty(keeperVal)) return keeperId;
    const donor = candidats.find((c) => !isEmpty(c.fields?.[field]));
    return donor ? donor.id : keeperId;
  };

  // Coche/décoche une valeur dans la combinaison d'un champ. À 0 valeur cochée
  // on retombe sur le choix par défaut, à 1 seule on repasse en choix simple.
  const toggleCombine = (field, id) => {
    setChoices((prev) => {
      const cur = prev[field];
      let arr = Array.isArray(cur) ? [...cur] : typeof cur === "string" && cur ? [cur] : [];
      arr = arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
      // Ne garde que les valeurs réellement présentes.
      const valid = nonEmptyIdsFor(field);
      arr = arr.filter((x) => valid.includes(x));
      if (arr.length === 0) return { ...prev, [field]: defaultChoiceFor(field) };
      if (arr.length === 1) return { ...prev, [field]: arr[0] };
      return { ...prev, [field]: arr };
    });
  };

  const isAllTaken = (field) => {
    const cur = choices[field];
    const ids = nonEmptyIdsFor(field);
    return Array.isArray(cur) && ids.length > 0 && ids.every((id) => cur.includes(id));
  };

  // "Tout prendre" coche toutes les valeurs non vides ; recliquer revient au
  // choix par défaut.
  const toggleTakeAll = (field) => {
    setChoices((prev) => {
      if (isAllTaken(field)) {
        return { ...prev, [field]: defaultChoiceFor(field) };
      }
      const ids = nonEmptyIdsFor(field);
      return { ...prev, [field]: ids.length > 1 ? ids : defaultChoiceFor(field) };
    });
  };

  const renderValueCell = (c, field, differs, combinable) => {
    const choice = choices[field];
    const selected = choice === c.id;
    const value = c.fields?.[field];
    const empty = isEmpty(value);
    // Valeur cochée dans une combinaison (2, 3… ou toutes).
    const combined = Array.isArray(choice) && choice.includes(c.id);
    const active = selected || combined;
    const clickable = !combinable || !empty;

    // Champ texte : une seule case à cocher (1 = prendre cette valeur,
    // plusieurs = les combiner). Champ atomique : radio, une seule valeur.
    const handleClick = () => {
      if (!clickable) return;
      if (combinable) {
        toggleCombine(field, c.id);
      } else {
        setChoices((prev) => ({ ...prev, [field]: c.id }));
      }
    };

    return (
      <Box
        key={c.id}
        onClick={handleClick}
        sx={{
          flex: 1,
          cursor: clickable ? "pointer" : "default",
          px: 1,
          py: 0.75,
          borderRadius: "10px",
          border: "1px solid",
          borderColor: active ? BRAND : "divider",
          borderStyle: combined ? "dashed" : "solid",
          bgcolor: active
            ? "#f0f7fd"
            : differs
            ? "#fffbeb"
            : "#fff",
          minWidth: 0,
          transition: "border-color .15s, background-color .15s",
          "&:hover": clickable ? { borderColor: BRAND } : {},
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          {combinable ? (
            <Checkbox
              size="small"
              checked={active}
              disabled={empty}
              title="Cochez une ou plusieurs valeurs à conserver"
              sx={{ p: 0, mt: "2px", color: BRAND, "&.Mui-checked": { color: BRAND } }}
            />
          ) : (
            <Radio
              size="small"
              checked={selected}
              sx={{ p: 0, mt: "2px", color: BRAND, "&.Mui-checked": { color: BRAND } }}
            />
          )}
          <Typography
            variant="body2"
            title={empty ? undefined : String(value)}
            sx={{
              flex: 1,
              wordBreak: "break-word",
              color: empty ? "text.disabled" : "text.primary",
              fontStyle: empty ? "italic" : "normal",
              display: "-webkit-box",
              WebkitLineClamp: combinable ? 6 : 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {empty ? "—" : displayValue(value)}
          </Typography>
        </Stack>
      </Box>
    );
  };

  // En-tête coloré d'une colonne candidat (avatar initiales + nom + badge gardé).
  const renderCandHeader = (c) => {
    const color = candColor(c.id);
    return (
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{ mb: 0.75, px: 0.75, py: 0.5, borderRadius: 1, bgcolor: `${color}14` }}
      >
        <Avatar sx={{ width: 22, height: 22, fontSize: 11, bgcolor: color }}>{initialsOf(c)}</Avatar>
        <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
          {fullName(c)}
        </Typography>
        {c.id === keeperId && (
          <Chip label="Gardé" size="small" sx={{ height: 16, fontSize: 10, bgcolor: BRAND, color: "#fff" }} />
        )}
      </Stack>
    );
  };

  // Carte d'une note (unique ou commune). Cliquer la carte coche/décoche.
  const renderNoteCard = (g) => {
    const kept = keepNotes[g.key] !== false;
    const toggle = () => setKeepNotes((prev) => ({ ...prev, [g.key]: prev[g.key] === false }));
    const common = g.candIds.length > 1;
    return (
      <Paper
        key={g.key}
        variant="outlined"
        onClick={toggle}
        sx={{
          p: 1,
          cursor: "pointer",
          borderRadius: 2,
          borderLeft: "4px solid",
          borderLeftColor: common ? BRAND : candColor(g.candIds[0]),
          bgcolor: "#fff",
          opacity: kept ? 1 : 0.45,
          transition: "opacity .15s, box-shadow .15s",
          "&:hover": { boxShadow: "0 3px 10px rgba(0,0,0,.1)" },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Checkbox
            size="small"
            checked={kept}
            onClick={(e) => e.stopPropagation()}
            onChange={toggle}
            sx={{ p: 0, mt: "2px", color: BRAND, "&.Mui-checked": { color: BRAND } }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                wordBreak: "break-word",
                maxHeight: 96,
                overflowY: "auto",
                textDecoration: kept ? "none" : "line-through",
              }}
            >
              {String(g.commentaire || "(vide)")}
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {g.auteur || "?"} {g.dateTime ? `— ${fmtDate(g.dateTime)}` : ""}
              </Typography>
              {common && (
                <>
                  {g.candIds.map((id) => (
                    <Box
                      key={id}
                      title={fullName(candidatById[id] || {})}
                      sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: candColor(id) }}
                    />
                  ))}
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`x${g.copies.length} → 1`}
                    title="Présente chez plusieurs candidats : une seule copie sera gardée"
                    sx={{ height: 16, fontSize: 10 }}
                  />
                </>
              )}
              {!common && g.copies.length > 1 && (
                <Chip size="small" variant="outlined" label={`x${g.copies.length} → 1`} sx={{ height: 16, fontSize: 10 }} />
              )}
            </Stack>
          </Box>
        </Stack>
      </Paper>
    );
  };

  // Carte d'un CV : icône selon le type, œil pour l'afficher dans la visionneuse.
  const renderFileCard = (f) => {
    const kept = keepFiles[f.id] !== false;
    const toggle = () => setKeepFiles((prev) => ({ ...prev, [f.id]: prev[f.id] === false }));
    const isPdf = String(f.name || "").toLowerCase().endsWith(".pdf");
    return (
      <Paper
        key={f.id}
        variant="outlined"
        sx={{
          p: 1,
          borderRadius: 2,
          borderLeft: "4px solid",
          borderLeftColor: candColor(f.candId),
          bgcolor: "#fff",
          opacity: kept ? 1 : 0.45,
          transition: "opacity .15s, box-shadow .15s",
          "&:hover": { boxShadow: "0 3px 10px rgba(0,0,0,.1)" },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Checkbox
            size="small"
            checked={kept}
            onChange={toggle}
            sx={{ p: 0, color: BRAND, "&.Mui-checked": { color: BRAND } }}
          />
          {isPdf ? (
            <PictureAsPdfIcon sx={{ color: "#D32F2F" }} fontSize="small" />
          ) : (
            <DescriptionIcon sx={{ color: BRAND }} fontSize="small" />
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" noWrap title={f.name}>
              {f.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {f.categorie || "CV"}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => openFile(f)} title="Afficher le fichier" disabled={previewLoading}>
            <VisibilityIcon fontSize="small" sx={{ color: BRAND }} />
          </IconButton>
        </Stack>
      </Paper>
    );
  };

  const renderCandidateDetails = (c) => (
    <Accordion key={c.id} disableGutters sx={{ boxShadow: "none", border: "1px solid", borderColor: "divider" }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle2">{fullName(c)}</Typography>
          {c.id === keeperId && (
            <Chip size="small" icon={<CheckCircleIcon />} label="Gardé" sx={{ bgcolor: BRAND, color: "#fff" }} />
          )}
          <Chip size="small" label={`${c.counts?.processes || 0} process`} variant="outlined" />
          <Chip size="small" label={`${c.counts?.tasks || 0} tâches`} variant="outlined" />
          <Chip size="small" label={`${c.counts?.notes || 0} notes`} variant="outlined" />
          <Chip size="small" label={`${c.counts?.files || 0} CV`} variant="outlined" />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="caption" color="text.secondary">
          {fmtDate(c.createdAt) ? `Créé le ${fmtDate(c.createdAt)} · ` : ""}Employeur : {c.employeur || "—"}
        </Typography>
        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" gutterBottom>Process ({c.processes?.length || 0})</Typography>
        {(c.processes || []).length === 0 ? (
          <Typography variant="body2" color="text.disabled">Aucun</Typography>
        ) : (
          c.processes.map((p) => (
            <Typography key={p.id} variant="body2">
              • {p.mission || "Process"} {p.createdAt ? `— ${fmtDate(p.createdAt)}` : ""}
            </Typography>
          ))
        )}
        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" gutterBottom>Tâches ({c.tasks?.length || 0})</Typography>
        {(c.tasks || []).length === 0 ? (
          <Typography variant="body2" color="text.disabled">Aucune</Typography>
        ) : (
          c.tasks.map((t) => (
            <Typography key={t.id} variant="body2">
              • {t.commentaire || "Tâche"} {t.dateTime ? `— ${fmtDate(t.dateTime)}` : ""} {t.statut ? <CheckCircleIcon sx={{ fontSize: 15, color: '#16a34a', verticalAlign: 'text-bottom' }} /> : null}
            </Typography>
          ))
        )}
        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" gutterBottom>Notes ({c.notes?.length || 0})</Typography>
        {(c.notes || []).length === 0 ? (
          <Typography variant="body2" color="text.disabled">Aucune</Typography>
        ) : (
          c.notes.map((n) => (
            <Typography key={n.id} variant="body2">
              • {n.commentaire || ""} {n.auteur ? `(${n.auteur})` : ""} {n.dateTime ? `— ${fmtDate(n.dateTime)}` : ""}
            </Typography>
          ))
        )}
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Dialog
      open={open}
      onClose={merging ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3, overflow: "hidden" } } }}
    >
      <Box sx={{ px: 3, py: 1.75, background: "#fff", borderBottom: "1px solid #e2edf7" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" rowGap={1}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                bgcolor: "#ebf4fb",
                color: BRAND,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CallMergeIcon />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
                Fusion des doublons
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                {loading ? "Chargement…" : `${candidats.length} fiches comparées → 1 candidat unique`}
              </Typography>
            </Box>
          </Stack>
          {!loading && keeperId && (
            <Stack direction="row" spacing={0.75} alignItems="center">
              {candidats.map((c) => (
                <Avatar
                  key={c.id}
                  title={fullName(c)}
                  sx={{
                    width: 30,
                    height: 30,
                    fontSize: 12,
                    fontWeight: 700,
                    bgcolor: candColor(c.id),
                    border: c.id === keeperId ? `2px solid ${BRAND}` : "2px solid #fff",
                    boxShadow: c.id === keeperId ? "0 0 0 2px #fff" : "none",
                    opacity: c.id === keeperId ? 1 : 0.7,
                  }}
                >
                  {initialsOf(c)}
                </Avatar>
              ))}
            </Stack>
          )}
        </Stack>
      </Box>
      <DialogContent dividers sx={{ bgcolor: PAGE_BG }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: BRAND }} />
          </Box>
        ) : (
          <>
            <SectionHeader num={1} title="Candidat à conserver" />
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              {candidats.map((c) => {
                const selected = keeperId === c.id;
                const color = candColor(c.id);
                return (
                  <Paper
                    key={c.id}
                    elevation={0}
                    onClick={() => setKeeperId(c.id)}
                    sx={{
                      cursor: "pointer",
                      px: 2,
                      py: 1.5,
                      borderRadius: 2.5,
                      minWidth: 215,
                      flex: "1 1 215px",
                      maxWidth: 280,
                      position: "relative",
                      border: "2px solid",
                      borderColor: selected ? BRAND : "transparent",
                      bgcolor: "#fff",
                      boxShadow: selected
                        ? "0 6px 18px rgba(3,64,123,.18)"
                        : "0 1px 3px rgba(0,0,0,.08)",
                      transition: "all .18s ease",
                      "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 16px rgba(0,0,0,.12)" },
                    }}
                  >
                    {selected && (
                      <CheckCircleIcon sx={{ position: "absolute", top: 8, right: 8, color: BRAND, fontSize: 20 }} />
                    )}
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar sx={{ bgcolor: color, width: 40, height: 40, fontWeight: 700 }}>
                        {initialsOf(c)}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                          {fullName(c)}
                        </Typography>
                        {fmtDate(c.createdAt) && (
                          <Typography variant="caption" color="text.secondary">
                            Créé le {fmtDate(c.createdAt)}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                      {[
                        [c.counts?.processes || 0, "proc"],
                        [c.counts?.tasks || 0, "tâches"],
                        [c.counts?.notes || 0, "notes"],
                        [c.counts?.files || 0, "CV"],
                      ].map(([n, lbl]) => (
                        <Chip
                          key={lbl}
                          size="small"
                          label={`${n} ${lbl}`}
                          sx={{
                            height: 20,
                            fontSize: 11,
                            bgcolor: n > 0 ? `${color}14` : "transparent",
                            color: n > 0 ? color : "text.disabled",
                            border: n > 0 ? "none" : "1px solid",
                            borderColor: "divider",
                          }}
                        />
                      ))}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>

            <Divider sx={{ my: 2.5 }} />
            <SectionHeader
              num={2}
              title="Champs : choisir les valeurs"
              right={
                <Stack direction="row" spacing={0.75} alignItems="center">
                  {[
                    { value: true, label: `Différences (${rows.filter((r) => r.differs).length})` },
                    { value: false, label: `Tous les champs (${rows.length})` },
                  ].map((o) => (
                    <Chip
                      key={String(o.value)}
                      size="small"
                      label={o.label}
                      onClick={() => setOnlyDiffs(o.value)}
                      sx={{
                        height: 26,
                        fontWeight: 600,
                        color: onlyDiffs === o.value ? "#fff" : "#475569",
                        bgcolor: onlyDiffs === o.value ? BRAND : "#f1f5f9",
                        "&:hover": { bgcolor: onlyDiffs === o.value ? "#02357a" : "#e2e8f0" },
                      }}
                    />
                  ))}
                </Stack>
              }
            />
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ color: "#64748b", fontSize: 12 }}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 14, height: 14, borderRadius: "4px", bgcolor: "#fffbeb", border: "1px solid #fcd34d" }} />
                <span>Valeurs différentes</span>
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 14, height: 14, borderRadius: "4px", bgcolor: "#f0f7fd", border: `1.5px solid ${BRAND}` }} />
                <span>Valeur gardée sur le candidat conservé</span>
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 14, height: 14, borderRadius: "4px", bgcolor: "#f0f7fd", border: `1.5px dashed ${BRAND}` }} />
                <span>Champs texte : cochez plusieurs valeurs pour les combiner</span>
              </Stack>
            </Stack>

            <Paper elevation={0} sx={{ mt: 1, p: 1.5, borderRadius: 2.5, bgcolor: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,.08)" }}>
              {/* En-tête colonnes avec avatars, aligné sur la grille */}
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  pl: "160px",
                  mb: 1,
                  pb: 1,
                  position: "sticky",
                  top: -17,
                  zIndex: 2,
                  bgcolor: "#fff",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                {candidats.map((c) => (
                  <Stack key={c.id} direction="row" spacing={0.75} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                    <Avatar sx={{ width: 22, height: 22, fontSize: 10, fontWeight: 700, bgcolor: candColor(c.id) }}>
                      {initialsOf(c)}
                    </Avatar>
                    <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }} noWrap>
                      {fullName(c)}
                    </Typography>
                    {c.id === keeperId && (
                      <Chip label="Gardé" size="small" sx={{ height: 16, fontSize: 10, bgcolor: BRAND, color: "#fff" }} />
                    )}
                  </Stack>
                ))}
              </Stack>
              {onlyDiffs && rows.some((r) => !r.differs) && (
                <Typography variant="caption" sx={{ display: "block", color: "#94a3b8", mt: 1 }}>
                  {rows.filter((r) => !r.differs).length} champ(s) identique(s) masqué(s) : leur valeur est conservée telle quelle.
                </Typography>
              )}
              {onlyDiffs && rows.every((r) => !r.differs) && (
                <Typography variant="body2" sx={{ color: "#16a34a", fontWeight: 600, py: 2, textAlign: "center" }}>
                  Toutes les valeurs sont identiques : aucun choix à faire.
                </Typography>
              )}
              {rows.filter((r) => !onlyDiffs || r.differs).map((r) => (
                <Stack key={r.field} direction="row" spacing={1} alignItems="stretch" sx={{ mb: 0.5 }}>
                  <Box
                    sx={{
                      width: 160,
                      flexShrink: 0,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "flex-start",
                      gap: 0.5,
                    }}
                  >
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {r.differs && <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#f59e0b", flexShrink: 0 }} />}
                      <Typography variant="body2" sx={{ fontWeight: r.differs ? 700 : 400, color: r.differs ? "#0f172a" : "#64748b" }}>
                        {r.label}
                      </Typography>
                    </Stack>
                    {r.combinable && (
                      <Chip
                        size="small"
                        icon={<CallMergeIcon />}
                        label="Tout prendre"
                        onClick={() => toggleTakeAll(r.field)}
                        variant={isAllTaken(r.field) ? "filled" : "outlined"}
                        sx={
                          isAllTaken(r.field)
                            ? { bgcolor: BRAND, color: "#fff", "& .MuiChip-icon": { color: "#fff" }, "&:hover": { bgcolor: "#02357a" } }
                            : { borderColor: BRAND, color: BRAND, "& .MuiChip-icon": { color: BRAND } }
                        }
                      />
                    )}
                  </Box>
                  {candidats.map((c) => renderValueCell(c, r.field, r.differs, r.combinable))}
                </Stack>
              ))}
            </Paper>

            <Divider sx={{ my: 2.5 }} />
            <SectionHeader
              num={3}
              title="Notes des doublons"
              right={
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={0.5}>
                  <Chip size="small" variant="outlined" label={`${noteGroups.length} uniques / ${totalNoteCopies} copies`} />
                  <Chip
                    size="small"
                    icon={<StickyNote2Icon sx={{ fontSize: 14 }} />}
                    label={`${keptNotesCount} conservée(s)`}
                    sx={{ bgcolor: "rgba(46,125,50,0.12)", color: "#2E7D32", fontWeight: 600, "& .MuiChip-icon": { color: "#2E7D32" } }}
                  />
                  <Button
                    size="small"
                    onClick={() => setKeepNotes(Object.fromEntries(noteGroups.map((g) => [g.key, true])))}
                  >
                    Tout garder
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setKeepNotes(Object.fromEntries(noteGroups.map((g) => [g.key, false])))}
                  >
                    Tout décocher
                  </Button>
                </Stack>
              }
            />
            <Typography variant="caption" color="text.secondary">
              Cliquez sur une carte pour la garder ou non. Les notes en double chez plusieurs candidats
              (pastilles de couleur, badge xN → 1) ne seront gardées qu'en un seul exemplaire.
            </Typography>

            {noteGroups.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 2, mt: 1, textAlign: "center" }}>
                <Typography variant="body2" color="text.disabled">Aucune note</Typography>
              </Paper>
            ) : (
              <>
                {commonNotes.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: BRAND }}>
                      Communes à plusieurs candidats ({commonNotes.length})
                    </Typography>
                    <Stack spacing={0.75} sx={{ mt: 0.5, maxHeight: 240, overflowY: "auto", pr: 0.5 }}>
                      {commonNotes.map(renderNoteCard)}
                    </Stack>
                  </Box>
                )}
                {Object.keys(uniqueNotesByCandidat).length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                      Propres à chaque candidat
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 0.5 }}>
                      {candidats.map((c) => (
                        <Box key={c.id} sx={{ flex: 1, minWidth: 0 }}>
                          {renderCandHeader(c)}
                          <Stack spacing={0.75} sx={{ maxHeight: 260, overflowY: "auto", pr: 0.5 }}>
                            {(uniqueNotesByCandidat[c.id] || []).length === 0 ? (
                              <Typography variant="caption" color="text.disabled" sx={{ px: 0.5 }}>
                                —
                              </Typography>
                            ) : (
                              uniqueNotesByCandidat[c.id].map(renderNoteCard)
                            )}
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </>
            )}

            <Divider sx={{ my: 2.5 }} />
            <SectionHeader
              num={4}
              title="CV des doublons"
              right={
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={0.5}>
                  <Chip size="small" variant="outlined" label={`${fileRows.length} fichier(s)`} />
                  <Chip
                    size="small"
                    icon={<PictureAsPdfIcon sx={{ fontSize: 14 }} />}
                    label={`${keptFilesCount} conservé(s)`}
                    sx={{ bgcolor: "rgba(46,125,50,0.12)", color: "#2E7D32", fontWeight: 600, "& .MuiChip-icon": { color: "#2E7D32" } }}
                  />
                  <Button
                    size="small"
                    onClick={() => setKeepFiles(Object.fromEntries(fileRows.map((f) => [f.id, true])))}
                  >
                    Tout garder
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setKeepFiles(Object.fromEntries(fileRows.map((f) => [f.id, false])))}
                  >
                    Tout décocher
                  </Button>
                </Stack>
              }
            />
            <Typography variant="caption" color="text.secondary">
              L'œil ouvre le CV dans la visionneuse pour le consulter avant de choisir.
            </Typography>

            {fileRows.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 2, mt: 1, textAlign: "center" }}>
                <Typography variant="body2" color="text.disabled">Aucun fichier</Typography>
              </Paper>
            ) : (
              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
                {candidats.map((c) => (
                  <Box key={c.id} sx={{ flex: 1, minWidth: 0 }}>
                    {renderCandHeader(c)}
                    <Stack spacing={0.75} sx={{ maxHeight: 220, overflowY: "auto", pr: 0.5 }}>
                      {(c.files || []).length === 0 ? (
                        <Typography variant="caption" color="text.disabled" sx={{ px: 0.5 }}>
                          —
                        </Typography>
                      ) : (
                        (c.files || []).map((f) => renderFileCard({ ...f, candId: c.id }))
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}

            <Divider sx={{ my: 2.5 }} />
            <SectionHeader num={5} title="Détail par candidat" />
            <Stack spacing={1}>{candidats.map(renderCandidateDetails)}</Stack>
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          bgcolor: "#fff",
          borderTop: "1px solid",
          borderColor: "divider",
          justifyContent: "space-between",
          flexWrap: "wrap",
          rowGap: 1,
        }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" rowGap={0.5}>
          {!loading && candidats.length > 0 && (
            <>
              <Chip
                size="small"
                icon={<CallMergeIcon sx={{ fontSize: 14 }} />}
                label={`${candidats.length} fiches → 1`}
                sx={{ bgcolor: "rgba(17,122,167,.1)", color: BRAND, fontWeight: 600, "& .MuiChip-icon": { color: BRAND } }}
              />
              <Chip size="small" variant="outlined" label={`${keptNotesCount}/${noteGroups.length} notes`} />
              <Chip size="small" variant="outlined" label={`${keptFilesCount}/${fileRows.length} CV`} />
            </>
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} disabled={merging} sx={{ color: "text.secondary" }}>
            Annuler
          </Button>
          <Button
            onClick={handleMerge}
            variant="contained"
            disabled={loading || merging || !keeperId || loserCount === 0}
            startIcon={<CallMergeIcon />}
            sx={{
              background: BRAND_GRAD,
              borderRadius: 2,
              px: 3,
              fontWeight: 700,
              boxShadow: "0 4px 12px rgba(17,122,167,.35)",
              "&:hover": { background: "#02357a" },
            }}
          >
            Fusionner
          </Button>
        </Stack>
      </DialogActions>

      <Dialog open={Boolean(preview)} onClose={closePreview} maxWidth="md" fullWidth>
        <DialogTitle sx={{ py: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              <PictureAsPdfIcon sx={{ color: "#D32F2F" }} />
              <Typography variant="subtitle1" noWrap title={preview?.name}>
                {preview?.name}
              </Typography>
            </Stack>
            <Button
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={() => preview?.url && window.open(preview.url, "_blank")}
            >
              Nouvel onglet
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, height: "75vh" }}>
          {preview?.url && (
            <iframe
              src={preview.url}
              title={preview.name}
              style={{ width: "100%", height: "100%", border: 0 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Modale de confirmation (remplace le window.confirm natif) */}
      <Dialog
        open={confirmOpen}
        onClose={merging ? undefined : () => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 3 } } }}
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningAmberIcon sx={{ color: "#e65100" }} />
            <span>Confirmer la fusion</span>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            Fusionner <b>{loserCount} doublon(s)</b> vers «&nbsp;
            <b>{fullName(candidatById[keeperId])}</b>&nbsp;» ?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Process, tâches et sélections seront rattachés au candidat gardé.
            {" "}{keptNotesCount}/{noteGroups.length} note(s) unique(s) et {keptFilesCount}/{fileRows.length} CV
            seront conservés ; les copies en double, les éléments décochés et les
            doublons seront supprimés.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, color: "#c62828" }}>
            Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={merging}>
            Annuler
          </Button>
          <Button
            onClick={doMerge}
            variant="contained"
            color="error"
            disabled={merging}
          >
            {merging ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Fusionner définitivement"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default MergeDialog;
